import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PageContent,
  Container,
  Row,
  Col,
  Heading,
  Button,
  ButtonIcon,
  Select,
  Table,
  Checkbox,
  ButtonSearch,
  Field,
  Modal,
} from 'components';
import { Margin } from 'styled-components-spacing';
import FileInput from '../../../../components/FileInput/FileInput';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import ReactDOM from 'react-dom';
import { useState } from 'react';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { chunk } from '../../../../helpers/ChunkLevelDomains';
import parseDate from '../../../../helpers/parseDate';
import RichTextEditor from 'react-rte';
import moment from 'moment';
import { questionGroups, questions } from '../../../../dictionaries/questionsAndGroups';
import {
  stabilityOptions,
  compliantStatusOptions,
  qualityStatusOptions,
  performanceStatusOptions,
  viabilityOptions,
  yesNoOptions,
} from '../../CisOptions';
import { fetchPdfCisReports } from '../../../../api/services/cis';
import checkPermissions from '../../../../helpers/checkPermissions';

const CertificationReportForm = ({
  appType,
  type,
  reports,
  loadReportDetails,
  id,
  updateReport,
  state,
  dispatch,
  updateFieldCreator,
  setInitialStateCreator,
  userPermissions,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [portalReady, setPortalReady] = useState(false);

  const modalInputRef = React.useRef(null);

  const history = useHistory();
  const isCvmbCis = history.location?.pathname?.includes('cvmb-cis') ?? false;
  const manageCisPermission =
    (isCvmbCis && checkPermissions({ roles: ['ipma_cis_cvmb'] }, userPermissions)) ||
    (!isCvmbCis && checkPermissions({ roles: ['ma_manage_cis'] }, userPermissions));

  const location = useLocation();
  const { t } = useTranslation();

  const handleChangeNative = (name, dataType) => (e) => {
    dispatch(updateFieldCreator({ name, dataType, value: e.target.value }));
  };

  const handleChangeCustom = (name, dataType) => (value) => {
    dispatch(updateFieldCreator({ name, dataType, value }));
  };

  const handleChangeAssessor = (name, category, type) => (e) => {
    dispatch(
      updateFieldCreator({ name, dataType: 'assessor', category, type, value: e.target.value }),
    );
  };

  const handleAddComplexRow = (name, row) => () => {
    dispatch(updateFieldCreator({ name, dataType: 'complex_add_row', row }));
  };

  const handleRemoveComplexRow = (name, rowIndex) => () => {
    dispatch(updateFieldCreator({ name, dataType: 'complex_remove_row', rowIndex }));
  };

  const handleChangeCell = (name, rowIndex, field) => (e) => {
    dispatch(
      updateFieldCreator({
        name,
        dataType: 'complex_cell',
        rowIndex,
        field,
        value: e.target.value,
      }),
    );
  };

  const handleChangeFileNative = (name) => (e) => {
    dispatch(
      updateFieldCreator({
        name,
        dataType: 'file',
        value: e.target.files && e.target.files.length ? e.target.files[0] : null,
      }),
    );
  };

  const handleClearFileNative = (name) => () => {
    dispatch(
      updateFieldCreator({
        name,
        dataType: 'file',
        value: null,
      }),
    );
  };

  const handleChangeSelect = (name, dataType = 'number') => (option) => {
    dispatch(updateFieldCreator({ name, dataType, value: option?.value }));
  };

  useEffect(() => {
    loadReportDetails(id, history);
  }, []);

  useEffect(() => {
    if (reports && reports.answers) {
      dispatch(setInitialStateCreator(reports.answers));
    }
  }, [reports]);

  useEffect(() => {
    if (document.getElementById(`${type}-cis-buttons-portal`)) {
      setPortalReady(true);
    }
  }, []);

  const action = useMemo(() => {
    if (!manageCisPermission) return 'view';

    const segments = location.pathname.split('/').filter(Boolean);
    return segments.at(-1);
  }, [location.pathname, manageCisPermission]);

  const levelRows = reports.certificationBody.levels
    ? chunk(reports.certificationBody.levels, 6)
    : [];

  const submit = (status) => {
    const formData = new FormData();
    const body = {
      status,
      certificationBody: reports.certificationBody.id,
    };

    if (appType === 'ma') {
      const answers = {};
      Object.entries(state)
        .filter(([_, entry]) => entry?.answer)
        .forEach(([_, entry]) => {
          const answer = entry?.answer;

          if (answer && entry?.id) {
            if (answer.file && answer.file instanceof File) {
              formData.append(`files[${entry.id}]`, answer.file);
            }

            answers[entry.id] = {
              questionId: entry.id,
              answerString:
                typeof answer.answerString === 'object' && answer.answerString?.toString
                  ? answer.answerString.toString('html')
                  : answer.answerString || null,
              answerBool: answer.answerBool ?? null,
              answerNumber: answer.answerNumber ?? null,
              answerComplex: answer.answerComplex ?? null,
              deleteFile: answer.deleteFile ?? false,
            };
          }
        });

      body.answers = answers;
    }

    if (appType === 'ipma') {
      const answers = {};
      Object.entries(state)
        .filter(([_, entry]) => entry?.answer)
        .forEach(([_, entry]) => {
          const answer = entry?.answer;
          if (answer && entry?.id) {
            answers[entry.id] = {
              questionId: entry.id,
              answerComment: answer.answerComment ?? '',
            };
          }
        });

      const groupStatus = Object.entries(state)
        .filter(([_, entry]) => entry?.status)
        .map(([_, entry]) => ({
          groupId: entry.id,
          status: entry.status,
        }));

      body.answers = answers;
      body.groupStatus = groupStatus;
    }

    formData.append('body', JSON.stringify(body));

    if (action === 'edit') {
      updateReport(id, formData, history);
    }
  };

  const maEditable = appType === 'ma' && (action === 'edit' || action === 'create');

  return (
    <PageContent>
      <Container>
        <Margin left="20" bottom="50">
          <Heading size="h2">{t('page.user.cis.certificationReports.report')}</Heading>
        </Margin>
        <Margin bottom="20">
          <Heading size="h5">{t('page.user.cis.certificationReports.generalInformation')}</Heading>
        </Margin>
        <Margin right="100" bottom="100">
          <Table>
            <Table.Body>
              <Table.Row key="country">
                <Table.Cell>{t('page.user.cis.certificationReports.country')}</Table.Cell>
                <Table.Cell>{reports.certificationBody.country.name}</Table.Cell>
              </Table.Row>
              <Table.Row key="memberAssociation">
                <Table.Cell>{t('page.user.cis.certificationReports.memberAssociation')}</Table.Cell>
                <Table.Cell>{reports.certificationBody.memberAssociation.name}</Table.Cell>
              </Table.Row>
              <Table.Row key="certificationBody">
                <Table.Cell>{t('page.user.cis.certificationReports.certificationBody')}</Table.Cell>
                <Table.Cell>{reports.certificationBody.name}</Table.Cell>
              </Table.Row>
              <Table.Row key="certificationBodyAddress">
                <Table.Cell>
                  {t('page.user.cis.certificationReports.certificationBodyAddress')}
                </Table.Cell>
                <Table.Cell>
                  {[
                    reports.certificationBody.address?.line1,
                    reports.certificationBody.address?.line2,
                    reports.certificationBody.address?.postCode,
                    reports.certificationBody.address?.city,
                    reports.certificationBody.address?.country?.name,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </Table.Cell>
              </Table.Row>
              <Table.Row key="headOfCB">
                <Table.Cell>{t('page.user.cis.certificationReports.headOfCB')}</Table.Cell>
                <Table.Cell>
                  {reports.certificationBody.cbHeadName}
                  {!isCvmbCis && (
                    <Link to={{ pathname: '/ma/cis/certification-bodies/details' }}>
                      <ButtonSearch
                        icon="edit"
                        color="black"
                        secondary
                        rounded
                        style={{
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          marginLeft: '5px',
                          height: '100%',
                        }}
                      />
                    </Link>
                  )}
                </Table.Cell>
              </Table.Row>
              <Table.Row key="headOfCBContactDetails">
                <Table.Cell>
                  {t('page.user.cis.certificationReports.headOfCBContactDetails')}
                </Table.Cell>
                <Table.Cell>
                  {reports.certificationBody.cbHeadTelephone},{' '}
                  {reports.certificationBody.cbHeadEmail}
                  {!isCvmbCis && (
                    <Link to={{ pathname: '/ma/cis/certification-bodies/details' }}>
                      <ButtonSearch
                        icon="edit"
                        color="black"
                        secondary
                        rounded
                        style={{
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          marginLeft: '5px',
                          height: '100%',
                        }}
                      />
                    </Link>
                  )}
                </Table.Cell>
              </Table.Row>
              <Table.Row key="contactPerson">
                <Table.Cell>{t('page.user.cis.certificationReports.contactPerson')}</Table.Cell>
                <Table.Cell>{reports.certificationBody.contactName}</Table.Cell>
              </Table.Row>
              <Table.Row key="dateOfReport">
                <Table.Cell>{t('page.user.cis.certificationReports.dateOfReport')}</Table.Cell>
                <Table.Cell>{moment(reports.dateOfReport).format('YYYY-MM-DD')}</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </Margin>
        <Margin bottom="20">
          <Heading size="h5">
            {t('page.user.cis.certificationReports.performanceAndSatisfaction')}
          </Heading>
        </Margin>
        <Margin left="50" bottom="100">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Heading>
                  {t('page.user.cis.certificationReports.performanceItem')}
                </Table.Heading>
                <Table.Heading>
                  {t('page.user.cis.certificationReports.referenceToICR')}
                </Table.Heading>
                <Table.Heading>
                  {t('page.user.cis.certificationReports.CBsHighlightsReport')}
                </Table.Heading>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <Table.Row>
                <Table.Cell style={{ verticalAlign: 'middle', textAlign: 'center' }} rowSpan={2}>
                  Number of certificates issued
                </Table.Cell>
                <Table.Cell style={{ verticalAlign: 'middle', textAlign: 'center' }} rowSpan={2}>
                  9.4.81
                </Table.Cell>
                <Table.Cell>
                  Comes from IPMA standard reporting by level,domain and by cerification
                  recertification
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>
                  <Table>
                    <Table.Head>
                      <Table.Row>
                        <Table.Heading style={{ textAlign: 'center' }} colSpan={2}>
                          Certificate
                        </Table.Heading>
                        <Table.Heading>New</Table.Heading>
                        <Table.Heading>Renew</Table.Heading>
                      </Table.Row>
                    </Table.Head>
                    <Table.Body>
                      {reports.certificationBody?.domains?.map((domain) =>
                        domain.levels?.map((level, idx) => (
                          <Table.Row key={`${domain.id}-${level.name}`}>
                            {idx === 0 && (
                              <Table.Cell
                                style={{ verticalAlign: 'middle', textAlign: 'center' }}
                                rowSpan={domain.levels.length}
                              >
                                {domain.name}
                              </Table.Cell>
                            )}
                            <Table.Cell>IPMA Level {level.name}</Table.Cell>
                            <Table.Cell>
                              {reports.certificates?.[level.levelDomainId]?.new || 0}
                            </Table.Cell>
                            <Table.Cell>
                              {reports.certificates?.[level.levelDomainId]?.renew || 0}
                            </Table.Cell>
                          </Table.Row>
                        )),
                      )}
                    </Table.Body>
                  </Table>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </Margin>
        <Margin bottom="20">
          <Heading size="h5">
            {t('page.user.cis.certificationReports.IPMACertificationSystemStatus')}
          </Heading>
        </Margin>
        <Margin left="50" top="50" bottom="50">
          {levelRows.map((rowLevels, rowIndex) => (
            <Margin bottom="20">
              <Row key={rowIndex}>
                {rowLevels.map((level) => (
                  <Col key={level.id} size={1 / 6}>
                    <Heading size="h5">{level.name}</Heading>
                    <hr />
                    {level.domains.map((domain) => (
                      <Checkbox selected={true} disabled key={domain.id}>
                        {domain.name}
                      </Checkbox>
                    ))}
                  </Col>
                ))}
              </Row>{' '}
            </Margin>
          ))}
        </Margin>
        <Margin left="50" right="100" bottom="100">
          <Table>
            <Table.Body>
              <Table.Row key="4LCAgreementSigned">
                <Table.Cell>
                  {t('page.user.cis.certificationReports.4LCAgreementSigned')}
                </Table.Cell>
                <Table.Cell>{parseDate(reports.certificationBody.lcAgreementSignedAt)}</Table.Cell>
              </Table.Row>
              <Table.Row key="IPMADeltaAgreementSigned">
                <Table.Cell>
                  {t('page.user.cis.certificationReports.IPMADeltaAgreementSigned')}
                </Table.Cell>
                <Table.Cell>
                  {parseDate(reports.certificationBody.deltaAgreementSignedAt)}
                </Table.Cell>
              </Table.Row>
              <Table.Row key="ISO9001Accreditation">
                <Table.Cell>
                  {t('page.user.cis.certificationReports.ISO9001AccreditationSigned')}
                </Table.Cell>
                <Table.Cell>
                  {parseDate(reports.certificationBody.iso9001AccreditationSignedAt)}
                </Table.Cell>
              </Table.Row>
              <Table.Row key="ISO17024Accreditation	">
                <Table.Cell>
                  {t('page.user.cis.certificationReports.ISO17024AccreditationSigned')}
                </Table.Cell>
                <Table.Cell>
                  {parseDate(reports.certificationBody.iso17024AccreditationSignedAt)}
                </Table.Cell>
              </Table.Row>
              <Table.Row key="4LCValidationStatus">
                <Table.Cell>
                  {t('page.user.cis.certificationReports.4LCValidationStatus')}
                </Table.Cell>
                <Table.Cell>{parseDate(reports.certificationBody.lcValidationSignedAt)}</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </Margin>
        <Margin bottom="20">
          <Heading size="h4">
            {t('page.user.cis.certificationReports.keyPerformanceIndicators')}
          </Heading>
        </Margin>
        <Margin bottom="20">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Heading size="h5">
              {t('page.user.cis.certificationReports.stabilityOfOrganisationManagement')}
            </Heading>
            <div style={{ width: '200px' }}>
              <Select
                small
                options={stabilityOptions}
                disabled={!(appType === 'ipma' && action === 'edit')}
                value={
                  stabilityOptions.find(
                    (option) =>
                      option.value ==
                      state[questionGroups.stabilityOfOrganisationManagement]?.status,
                  ) || stabilityOptions[0]
                }
                onChange={handleChangeSelect(
                  questionGroups.stabilityOfOrganisationManagement,
                  'status',
                )}
              />
            </div>
          </div>
        </Margin>
        <Margin left="20" bottom="100">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Heading>
                  {t('page.user.cis.certificationReports.performanceItem')}
                </Table.Heading>
                <Table.Heading>
                  {t('page.user.cis.certificationReports.referenceToICR')}
                </Table.Heading>
                <Table.Heading>
                  {t('page.user.cis.certificationReports.CBsHighlightsReport')}
                </Table.Heading>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <Table.Row>
                <Table.Cell style={{ verticalAlign: 'middle' }}>
                  {t('page.user.cis.certificationReports.CBsCurrentOrganisation')}
                </Table.Cell>
                <Table.Cell style={{ verticalAlign: 'middle' }}>
                  Chapter 5 + 6 and 4.1.1, 5.1.2
                </Table.Cell>
                <Table.Cell>
                  {t('page.user.cis.certificationReports.attachAnOrganizationChart')}
                  {maEditable ? (
                    <>
                      <RichTextEditor
                        value={
                          state[questions.cbCurrentOrg]?.answer?.answerString ||
                          RichTextEditor.createEmptyValue()
                        }
                        onChange={handleChangeCustom(questions.cbCurrentOrg, 'string')}
                        editorStyle={{
                          height: '300px',
                        }}
                      />
                      <div style={{ width: '200px', marginTop: 10 }}>
                        <FileInput
                          fileName={
                            state[questions.cbCurrentOrg]?.answer?.file
                              ? state[questions.cbCurrentOrg]?.answer?.file?.name ||
                                state[questions.cbCurrentOrg]?.answer?.file?.fileName
                              : ''
                          }
                          clear={handleClearFileNative(questions.cbCurrentOrg)}
                          onChange={handleChangeFileNative(questions.cbCurrentOrg)}
                          placeholder="Add a file"
                        />
                      </div>
                    </>
                  ) : (
                    <div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            state[questions.cbCurrentOrg]?.answer?.answerString?.toString('html') ||
                            '',
                        }}
                      />
                      {state[questions.cbCurrentOrg]?.answer?.file?.fileName && (
                        <div style={{ marginTop: 10 }}>
                          <a
                            style={{
                              color: 'blue',
                              textDecoration: 'underline',
                              fontWeight: 'bold',
                            }}
                            href={state[questions.cbCurrentOrg]?.answer?.file?.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {state[questions.cbCurrentOrg]?.answer?.file?.fileName ||
                              'View attached file'}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </Table.Cell>
                <Margin left="50">
                  <ButtonSearch
                    icon="comment"
                    color={
                      state[questions.cbCurrentOrg]?.answer?.answerComment && appType === 'ma'
                        ? 'red'
                        : 'black'
                    }
                    secondary
                    rounded
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                    disabled={
                      !state[questions.cbCurrentOrg]?.answer?.answerComment &&
                      !(appType === 'ipma' && action === 'edit')
                    }
                    onClick={() => {
                      setSelectedQuestion(state[questions.cbCurrentOrg]);
                      setIsModalOpen(true);
                    }}
                  />
                </Margin>
              </Table.Row>
              <Table.Row>
                <Table.Cell>{t('page.user.cis.certificationReports.CBsChanges')}</Table.Cell>
                <Table.Cell>6.4.1</Table.Cell>
                <Table.Cell>
                  <Field>
                    <input
                      placeholder="Write something in"
                      readOnly={appType === 'ipma' || !maEditable}
                      value={state[questions.cbChanges]?.answer?.answerString || ''}
                      onChange={handleChangeNative(questions.cbChanges, 'string')}
                    />
                  </Field>
                </Table.Cell>
                <Margin left="50">
                  <ButtonSearch
                    icon="comment"
                    color={
                      state[questions.cbChanges]?.answer?.answerComment && appType === 'ma'
                        ? 'red'
                        : 'black'
                    }
                    secondary
                    rounded
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                    disabled={
                      !state[questions.cbChanges]?.answer?.answerComment &&
                      !(appType === 'ipma' && action === 'edit')
                    }
                    onClick={() => {
                      setSelectedQuestion(state[questions.cbChanges]);
                      setIsModalOpen(true);
                    }}
                  />
                </Margin>
              </Table.Row>
              <Table.Row>
                <Table.Cell>
                  {t('page.user.cis.certificationReports.CBsCurrentStateOfStability')}
                </Table.Cell>
                <Table.Cell>6.1, 6.2, 6.3, 5.2, 7.2, 7.3, 6.4.4, 6.4.5</Table.Cell>
                <Table.Cell>
                  Describe the major issues/risks and measures taken which is effecting your CB. You
                  may wish to consider the following topics:
                  <Margin left="50" bottom="20">
                    <ul style={{ listStyleType: 'disc' }}>
                      <li>Confidentiality</li>
                      <li>Security</li>
                      <li>Cessation of Certification activity</li>
                      <li>Conflict of interest and impartiality</li>
                      <li>Changes/issues in management, key staff (resources) and assessors</li>
                      <li>Continuity</li>
                      <li>Procedures or processes</li>
                    </ul>
                  </Margin>
                  <Field>
                    <input
                      placeholder="Write something in"
                      readOnly={appType === 'ipma' || !maEditable}
                      value={state[questions.cbOrganisationStability]?.answer?.answerString || ''}
                      onChange={handleChangeNative(questions.cbOrganisationStability, 'string')}
                    />
                  </Field>
                </Table.Cell>
                <Margin left="50">
                  <ButtonSearch
                    icon="comment"
                    color={
                      state[questions.cbOrganisationStability]?.answer?.answerComment &&
                      appType === 'ma'
                        ? 'red'
                        : 'black'
                    }
                    secondary
                    rounded
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                    disabled={
                      !state[questions.cbOrganisationStability]?.answer?.answerComment &&
                      !(appType === 'ipma' && action === 'edit')
                    }
                    onClick={() => {
                      setSelectedQuestion(state[questions.cbOrganisationStability]);
                      setIsModalOpen(true);
                    }}
                  />
                </Margin>
              </Table.Row>
            </Table.Body>
          </Table>
        </Margin>
        <Margin bottom="20">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Heading size="h5">
              {t('page.user.cis.certificationReports.financialViability')}
            </Heading>
            <div style={{ width: '200px' }}>
              <Select
                value={
                  viabilityOptions.find(
                    (option) => option.value == state[questionGroups.financialViability]?.status,
                  ) || viabilityOptions[0]
                }
                onChange={handleChangeSelect(questionGroups.financialViability, 'status')}
                options={viabilityOptions}
                small
                disabled={!(appType === 'ipma' && action === 'edit')}
              />
            </div>
          </div>
        </Margin>
        <Margin left="20" bottom="100">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Heading>
                  {t('page.user.cis.certificationReports.performanceItem')}
                </Table.Heading>
                <Table.Heading>
                  {t('page.user.cis.certificationReports.referenceToICR')}
                </Table.Heading>
                <Table.Heading>
                  {t('page.user.cis.certificationReports.CBsHighlightsReport')}
                </Table.Heading>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <Table.Row>
                <Table.Cell>
                  {t('page.user.cis.certificationReports.CBsCurrentFinancialViability')}
                </Table.Cell>
                <Table.Cell>4.1.3, 6.1.18,10.1.1</Table.Cell>
                <Table.Cell>
                  Does the CB have the financial resources and facilities to enable it to deliver
                  and complete a certification cycle it has advertised and accepted candidates for?
                  <Margin bottom="20">
                    <Select
                      options={yesNoOptions}
                      value={yesNoOptions.find(
                        (option) =>
                          option.value ==
                          state[questions.cbFinancialViability]?.answer?.answerNumber,
                      )}
                      onChange={handleChangeSelect(questions.cbFinancialViability)}
                      disabled={appType === 'ipma' || !maEditable}
                    />
                  </Margin>
                  <Field>
                    <input
                      readOnly={appType === 'ipma' || !maEditable}
                      value={state[questions.cbFinancialViability]?.answer?.answerString || ''}
                      onChange={handleChangeNative(questions.cbFinancialViability, 'string')}
                      placeholder="Write something in"
                    />
                  </Field>
                </Table.Cell>
                <Margin left="50">
                  <ButtonSearch
                    icon="comment"
                    color={
                      state[questions.cbFinancialViability]?.answer?.answerComment &&
                      appType === 'ma'
                        ? 'red'
                        : 'black'
                    }
                    secondary
                    rounded
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                    disabled={
                      !state[questions.cbFinancialViability]?.answer?.answerComment &&
                      !(appType === 'ipma' && action === 'edit')
                    }
                    onClick={() => {
                      setSelectedQuestion(state[questions.cbFinancialViability]);
                      setIsModalOpen(true);
                    }}
                  />
                </Margin>
              </Table.Row>
            </Table.Body>
          </Table>
        </Margin>
        <Margin bottom="20">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Heading size="h5">
              {t('page.user.cis.certificationReports.3performanceAndSatisfaction')}
            </Heading>
            <div style={{ width: '200px' }}>
              <Select
                disabled={!(appType === 'ipma' && action === 'edit')}
                value={
                  performanceStatusOptions.find(
                    (option) =>
                      option.value == state[questionGroups.performanceAndSatisfaction]?.status,
                  ) || performanceStatusOptions[0]
                }
                small
                options={performanceStatusOptions}
                onChange={handleChangeSelect(questionGroups.performanceAndSatisfaction, 'status')}
              />
            </div>
          </div>
        </Margin>
        <Margin left="20" bottom="20">
          <Row>
            <Col size={1 / 6}>Complaints and appeals</Col>
            <Col size={1 / 6}>9.6.6, 5.1.22, 5.1.23</Col>
            <Col size={3 / 6}>
              Report Complaints and appeals
              <br /> <label>No. of complaints received:</label>
              <Field>
                <textarea
                  value={state[questions.complaintsAppeals]?.answer?.answerString}
                  onChange={handleChangeNative(questions.complaintsAppeals, 'string')}
                  placeholder="Write something in"
                  readOnly={appType === 'ipma' || !maEditable}
                />
              </Field>
            </Col>
            <Col size={1 / 6}>
              <Margin left="50">
                <ButtonSearch
                  icon="comment"
                  color={
                    state[questions.complaintsAppeals]?.answer?.answerComment && appType === 'ma'
                      ? 'red'
                      : 'black'
                  }
                  secondary
                  rounded
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }}
                  disabled={
                    !state[questions.complaintsAppeals]?.answer?.answerComment &&
                    !(appType === 'ipma' && action === 'edit')
                  }
                  onClick={() => {
                    setSelectedQuestion(state[questions.complaintsAppeals]);
                    setIsModalOpen(true);
                  }}
                />
              </Margin>
            </Col>
          </Row>
          <hr />
        </Margin>
        <Margin left="20" bottom="20">
          <Row>
            <Col size={1 / 6}>CB’s results of candidate feedback</Col>
            <Col size={1 / 6}>9.4.91</Col>
            <Col size={3 / 6}>
              Summary of Candidate Feedback report
              <br />
              <Field>
                <textarea
                  value={state[questions.candidateFeedback]?.answer?.answerString}
                  onChange={handleChangeNative(questions.candidateFeedback, 'string')}
                  placeholder="Write something in"
                  readOnly={appType === 'ipma' || !maEditable}
                />
              </Field>
            </Col>
            <Col size={1 / 6}>
              <Margin left="50">
                <ButtonSearch
                  icon="comment"
                  color={
                    state[questions.candidateFeedback]?.answer?.answerComment && appType === 'ma'
                      ? 'red'
                      : 'black'
                  }
                  secondary
                  rounded
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }}
                  disabled={
                    !state[questions.candidateFeedback]?.answer?.answerComment &&
                    !(appType === 'ipma' && action === 'edit')
                  }
                  onClick={() => {
                    setSelectedQuestion(state[questions.candidateFeedback]);
                    setIsModalOpen(true);
                  }}
                />
              </Margin>
            </Col>
          </Row>
          <hr />
        </Margin>
        <Margin left="20" bottom="20">
          <Row>
            <Col size={1 / 6}>CB’s status of the GCs</Col>
            <Col size={1 / 6}>9.9.10</Col>
            <Col size={3 / 6}>
              CB’s status of the GCs
              <br />
              <Field>
                <textarea
                  value={state[questions.statusGc]?.answer?.answerString}
                  onChange={handleChangeNative(questions.statusGc, 'string')}
                  placeholder="Write something in"
                  readOnly={appType === 'ipma' || !maEditable}
                />
              </Field>
            </Col>
            <Col size={1 / 6}>
              <Margin left="50">
                <ButtonSearch
                  icon="comment"
                  color={
                    state[questions.statusGc]?.answer?.answerComment && appType === 'ma'
                      ? 'red'
                      : 'black'
                  }
                  secondary
                  rounded
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }}
                  disabled={
                    !state[questions.statusGc]?.answer?.answerComment &&
                    !(appType === 'ipma' && action === 'edit')
                  }
                  onClick={() => {
                    setSelectedQuestion(state[questions.statusGc]);
                    setIsModalOpen(true);
                  }}
                />
              </Margin>
            </Col>
          </Row>
          <hr />
        </Margin>
        <Margin left="20" bottom="20">
          <Row>
            <Col size={1 / 6}>CB’s state the current performance satisfaction</Col>
            <Col size={1 / 6}></Col>
            <Col size={3 / 6}>
              Describe the major issues/risks and measures taken which is effecting your CB. You may
              wish to consider the following topics:
              <Margin left="50" bottom="20">
                <ul style={{ listStyleType: 'disc' }}>
                  <li>No of certificates</li>
                  <li>Complaints and appeals</li>
                  <li>Candidates feedback</li>
                </ul>
              </Margin>
              <Field>
                <textarea
                  value={state[questions.performanceSatisfaction]?.answer?.answerString}
                  onChange={handleChangeNative(questions.performanceSatisfaction, 'string')}
                  placeholder="Write something in"
                  readOnly={appType === 'ipma' || !maEditable}
                />
              </Field>
            </Col>
            <Col size={1 / 6}>
              <Margin left="50">
                <ButtonSearch
                  icon="comment"
                  color={
                    state[questions.performanceSatisfaction]?.answer?.answerComment &&
                    appType === 'ma'
                      ? 'red'
                      : 'black'
                  }
                  secondary
                  rounded
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }}
                  disabled={
                    !state[questions.performanceSatisfaction]?.answer?.answerComment &&
                    !(appType === 'ipma' && action === 'edit')
                  }
                  onClick={() => {
                    setSelectedQuestion(state[questions.performanceSatisfaction]);
                    setIsModalOpen(true);
                  }}
                />
              </Margin>
            </Col>
          </Row>
          <hr />
        </Margin>
        <Margin left="20" bottom="100">
          <Row>
            <Col size={1 / 6}>GC’s satisfaction level</Col>
            <Col size={1 / 6}>9.9.10</Col>
            <Col size={3 / 6}>
              Summary of Feedback Report
              <br />
              <Field>
                <textarea
                  value={state[questions.satisfactionLevelGc]?.answer?.answerString}
                  onChange={handleChangeNative(questions.satisfactionLevelGc, 'string')}
                  placeholder="Write something in"
                  readOnly={appType === 'ipma' || !maEditable}
                />
              </Field>
            </Col>
            <Col size={1 / 6}>
              <Margin left="50">
                <ButtonSearch
                  icon="comment"
                  color={
                    state[questions.satisfactionLevelGc]?.answer?.answerComment && appType === 'ma'
                      ? 'red'
                      : 'black'
                  }
                  secondary
                  rounded
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }}
                  disabled={
                    !state[questions.satisfactionLevelGc]?.answer?.answerComment &&
                    !(appType === 'ipma' && action === 'edit')
                  }
                  onClick={() => {
                    setSelectedQuestion(state[questions.satisfactionLevelGc]);
                    setIsModalOpen(true);
                  }}
                />
              </Margin>
            </Col>
          </Row>
          <hr />
        </Margin>
        <Margin bottom="20">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Heading size="h5">
              {t('page.user.cis.certificationReports.qualityOfCertificationProcess')}
            </Heading>
            <div style={{ width: '200px' }}>
              <Select
                disabled={!(appType === 'ipma' && action === 'edit')}
                small
                value={
                  qualityStatusOptions.find(
                    (option) =>
                      option.value == state[questionGroups.qualityOfCertificationProcess]?.status,
                  ) || qualityStatusOptions[0]
                }
                options={qualityStatusOptions}
                onChange={handleChangeSelect(
                  questionGroups.qualityOfCertificationProcess,
                  'status',
                )}
              />
            </div>
          </div>
        </Margin>
        <Margin left="20" bottom="20">
          a) Changes in Certification Scheme
        </Margin>
        <Margin left="20" bottom="100">
          <div style={{ display: 'flex' }}>
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Heading>
                    {t('page.user.cis.certificationReports.performanceItem')}
                  </Table.Heading>
                  <Table.Heading>
                    {t('page.user.cis.certificationReports.referenceToICR')}
                  </Table.Heading>
                  <Table.Heading>
                    {t('page.user.cis.certificationReports.CBsHighlightsReport')}
                  </Table.Heading>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                <Table.Row>
                  <Table.Cell>
                    CB’s changes in the certification scheme, paths and assessment methods
                  </Table.Cell>
                  <Table.Cell>
                    8.1.3, 8.1.6, 8.2.2, 9.2.3, 9.5.9, 9.8.2, 9.8.3, 9.2.4, 9.5.8, 9.4.90, 9.7, ICR
                    tables 6 and 11,
                  </Table.Cell>
                  <Table.Cell>
                    Report any changes in your certification, for example procedures, scheme,
                    language, additions, RPL&A’s, selection of assessment paths, translation of ICB,
                    withdrawal of certificates, regional CB, VWBC use method
                    <Field>
                      <textarea
                        value={state[questions.schemePathAssessmentChanges]?.answer?.answerString}
                        onChange={handleChangeNative(
                          questions.schemePathAssessmentChanges,
                          'string',
                        )}
                        placeholder="Write something in"
                        readOnly={appType === 'ipma' || !maEditable}
                      />
                    </Field>
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
            <Margin left="50">
              <ButtonSearch
                icon="comment"
                color={
                  state[questions.schemePathAssessmentChanges]?.answer?.answerComment &&
                  appType === 'ma'
                    ? 'red'
                    : 'black'
                }
                secondary
                rounded
                style={{
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                }}
                disabled={
                  !state[questions.schemePathAssessmentChanges]?.answer?.answerComment &&
                  !(appType === 'ipma' && action === 'edit')
                }
                onClick={() => {
                  setSelectedQuestion(state[questions.schemePathAssessmentChanges]);
                  setIsModalOpen(true);
                }}
              />
            </Margin>
          </div>
          <hr />
          <Margin left="20" top="20" bottom="20">
            b) Overview of Assesor Network
          </Margin>
          <Margin bottom="20">
            <div style={{ display: 'flex' }}>
              <Table>
                <Table.Head>
                  <Table.Row>
                    <Table.Heading>
                      {t('page.user.cis.certificationReports.performanceItem')}
                    </Table.Heading>
                    <Table.Heading>
                      {t('page.user.cis.certificationReports.referenceToICR')}
                    </Table.Heading>
                    <Table.Heading>Type of assessors -6.3.5</Table.Heading>
                    <Table.Heading>Number of assessors</Table.Heading>
                    <Table.Heading>Number of new (recruited) assessors- 6.3.1</Table.Heading>
                    <Table.Heading>Number of released assessors – 6.3.37</Table.Heading>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell
                      style={{ verticalAlign: 'middle', textAlign: 'center' }}
                      rowSpan={4}
                    >
                      Overview of assessor network
                    </Table.Cell>
                    <Table.Cell
                      style={{ verticalAlign: 'middle', textAlign: 'center' }}
                      rowSpan={4}
                    >
                      6.3.1, 6.3.5, 6.3.28, 6.3.37
                    </Table.Cell>
                    <Table.Cell>International Certification Advisor (ICA)</Table.Cell>
                    <Table.Cell>
                      <Field>
                        <input
                          readOnly={appType === 'ipma' || !maEditable}
                          type="number"
                          value={state[questions.assessors]?.answer?.answerComplex?.ica?.total}
                          onChange={handleChangeAssessor(questions.assessors, 'ica', 'total')}
                          placeholder="Number"
                        />
                      </Field>
                    </Table.Cell>
                    <Table.Cell>
                      <Field>
                        <input
                          readOnly={appType === 'ipma' || !maEditable}
                          type="number"
                          value={state[questions.assessors]?.answer?.answerComplex?.ica?.new}
                          onChange={handleChangeAssessor(questions.assessors, 'ica', 'new')}
                          placeholder="Number"
                        />
                      </Field>
                    </Table.Cell>
                    <Table.Cell>
                      <Field>
                        <input
                          readOnly={appType === 'ipma' || !maEditable}
                          type="number"
                          value={state[questions.assessors]?.answer?.answerComplex?.ica?.released}
                          onChange={handleChangeAssessor(questions.assessors, 'ica', 'released')}
                          placeholder="Number"
                        />
                      </Field>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>National First Assessor (NFA)</Table.Cell>
                    <Table.Cell>
                      <Field>
                        <input
                          readOnly={appType === 'ipma' || !maEditable}
                          type="number"
                          value={state[questions.assessors]?.answer?.answerComplex?.nfa?.total}
                          onChange={handleChangeAssessor(questions.assessors, 'nfa', 'total')}
                          placeholder="Number"
                        />
                      </Field>
                    </Table.Cell>
                    <Table.Cell>
                      <Field>
                        <input
                          type="number"
                          readOnly={appType === 'ipma' || !maEditable}
                          value={state[questions.assessors]?.answer?.answerComplex?.nfa?.new}
                          onChange={handleChangeAssessor(questions.assessors, 'nfa', 'new')}
                          placeholder="Number"
                        />
                      </Field>
                    </Table.Cell>
                    <Table.Cell>
                      <Field>
                        <input
                          type="number"
                          value={state[questions.assessors]?.answer?.answerComplex?.nfa?.released}
                          onChange={handleChangeAssessor(questions.assessors, 'nfa', 'released')}
                          placeholder="Number"
                        />
                      </Field>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>National Lead/Co – Assessor (NLA/NCA)</Table.Cell>
                    <Table.Cell>
                      <Field>
                        <input
                          type="number"
                          readOnly={appType === 'ipma' || !maEditable}
                          value={state[questions.assessors]?.answer?.answerComplex?.nla?.total}
                          onChange={handleChangeAssessor(questions.assessors, 'nla', 'total')}
                          placeholder="Number"
                        />
                      </Field>
                    </Table.Cell>
                    <Table.Cell>
                      <Field>
                        <input
                          type="number"
                          value={state[questions.assessors]?.answer?.answerComplex?.nla?.new}
                          onChange={handleChangeAssessor(questions.assessors, 'nla', 'new')}
                          placeholder="Number"
                        />
                      </Field>
                    </Table.Cell>
                    <Table.Cell>
                      <Field>
                        <input
                          type="number"
                          readOnly={appType === 'ipma' || !maEditable}
                          value={state[questions.assessors]?.answer?.answerComplex?.nla?.released}
                          onChange={handleChangeAssessor(questions.assessors, 'nla', 'released')}
                          placeholder="Number"
                        />
                      </Field>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>National Probationary Assessor</Table.Cell>
                    <Table.Cell>
                      <Field>
                        <input
                          readOnly={appType === 'ipma' || !maEditable}
                          type="number"
                          value={state[questions.assessors]?.answer?.answerComplex?.npa?.total}
                          onChange={handleChangeAssessor(questions.assessors, 'npa', 'total')}
                          placeholder="Number"
                        />
                      </Field>
                    </Table.Cell>
                    <Table.Cell>
                      <Field>
                        <input
                          readOnly={appType === 'ipma' || !maEditable}
                          type="number"
                          value={state[questions.assessors]?.answer?.answerComplex?.npa?.new}
                          onChange={handleChangeAssessor(questions.assessors, 'npa', 'new')}
                          placeholder="Number"
                        />
                      </Field>
                    </Table.Cell>
                    <Table.Cell>
                      <Field>
                        <input
                          type="number"
                          readOnly={appType === 'ipma' || !maEditable}
                          value={state[questions.assessors]?.answer?.answerComplex?.npa?.released}
                          onChange={handleChangeAssessor(questions.assessors, 'npa', 'released')}
                          placeholder="Number"
                        />
                      </Field>
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
              <Margin left="50">
                <ButtonSearch
                  icon="comment"
                  color={
                    state[questions.assessors]?.answer?.answerComment && appType === 'ma'
                      ? 'red'
                      : 'black'
                  }
                  secondary
                  rounded
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }}
                  disabled={
                    !state[questions.assessors]?.answer?.answerComment &&
                    !(appType === 'ipma' && action === 'edit')
                  }
                  onClick={() => {
                    setSelectedQuestion(state[questions.assessors]);
                    setIsModalOpen(true);
                  }}
                />
              </Margin>
            </div>
          </Margin>
          <Margin left="20" bottom="20">
            c) Assessor Professional Development
          </Margin>
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Heading>
                  {t('page.user.cis.certificationReports.performanceItem')}
                </Table.Heading>
                <Table.Heading>
                  {t('page.user.cis.certificationReports.referenceToICR')}
                </Table.Heading>
                <Table.Heading>
                  {t('page.user.cis.certificationReports.CBsHighlightsReport')}
                </Table.Heading>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <Table.Row>
                <Table.Cell style={{ verticalAlign: 'middle' }}>Development plan</Table.Cell>
                <Table.Cell style={{ verticalAlign: 'middle' }}></Table.Cell>
                <Table.Cell>
                  Attach or describe assessor development plan
                  {maEditable ? (
                    <>
                      <RichTextEditor
                        value={
                          state[questions.developmentPlan]?.answer?.answerString ||
                          RichTextEditor.createEmptyValue()
                        }
                        onChange={handleChangeCustom(questions.developmentPlan, 'string')}
                        editorStyle={{
                          height: '300px',
                        }}
                      />
                      <div style={{ width: '200px', marginTop: 10 }}>
                        <FileInput
                          onChange={handleChangeFileNative(questions.developmentPlan)}
                          clear={handleClearFileNative(questions.developmentPlan)}
                          fileName={
                            state[questions.developmentPlan]?.answer?.file?.name ||
                            state[questions.developmentPlan]?.answer?.file?.fileName ||
                            ''
                          }
                          placeholder="Add a file"
                        />
                      </div>
                    </>
                  ) : (
                    <div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            state[questions.developmentPlan]?.answer?.answerString?.toString(
                              'html',
                            ) || '',
                        }}
                      />
                      {state[questions.developmentPlan]?.answer?.file?.fileName && (
                        <div style={{ marginTop: 8 }}>
                          <a
                            href={state[questions.developmentPlan]?.answer?.file?.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {state[questions.developmentPlan]?.answer?.file?.fileName}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Workshop dates</Table.Cell>
                <Table.Cell></Table.Cell>
                <Table.Cell>
                  Provide assessor workshop dates{' '}
                  <Field>
                    <textarea
                      value={state[questions.workshopDates]?.answer?.answerString}
                      onChange={handleChangeNative(questions.workshopDates, 'string')}
                      placeholder="Write something in"
                      readOnly={appType === 'ipma' || !maEditable}
                    />
                  </Field>
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell style={{ verticalAlign: 'middle' }}>Workshop agenda</Table.Cell>
                <Table.Cell style={{ verticalAlign: 'middle' }}></Table.Cell>
                <Table.Cell>
                  Attach or describe assessor development plan
                  {maEditable ? (
                    <>
                      <RichTextEditor
                        value={
                          state[questions.workshopAgenda]?.answer?.answerString ||
                          RichTextEditor.createEmptyValue()
                        }
                        onChange={handleChangeCustom(questions.workshopAgenda, 'string')}
                        editorStyle={{
                          height: '300px',
                        }}
                      />
                      <div style={{ width: '200px', marginTop: 10 }}>
                        <FileInput
                          onChange={handleChangeFileNative(questions.workshopAgenda)}
                          clear={handleClearFileNative(questions.workshopAgenda)}
                          fileName={
                            state[questions.workshopAgenda]?.answer?.file?.name ||
                            state[questions.workshopAgenda]?.answer?.file?.fileName ||
                            ''
                          }
                          placeholder="Add a file"
                        />
                      </div>
                    </>
                  ) : (
                    <div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            state[questions.workshopDates]?.answer?.answerString?.toString(
                              'html',
                            ) || '',
                        }}
                      />
                      {state[questions.workshopDates]?.answer?.file?.fileName && (
                        <div style={{ marginTop: 8 }}>
                          <a
                            href={state[questions.workshopDates]?.answer?.file?.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {state[questions.workshopDates]?.answer?.file?.fileName}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </Table.Cell>
                <Margin left="50">
                  <ButtonSearch
                    icon="comment"
                    color={
                      state[questions.workshopAgenda]?.answer?.answerComment && appType === 'ma'
                        ? 'red'
                        : 'black'
                    }
                    secondary
                    rounded
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                    disabled={
                      !state[questions.workshopAgenda]?.answer?.answerComment &&
                      !(appType === 'ipma' && action === 'edit')
                    }
                    onClick={() => {
                      setSelectedQuestion(state[questions.workshopAgenda]);
                      setIsModalOpen(true);
                    }}
                  />
                </Margin>
              </Table.Row>
            </Table.Body>
          </Table>
        </Margin>
        <Margin bottom="20">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Heading size="h5">
              {t('page.user.cis.certificationReports.complianceQualityAssurance')}
            </Heading>
            <div style={{ width: '200px' }}>
              <Select
                disabled={!(appType === 'ipma' && action === 'edit')}
                small
                value={
                  compliantStatusOptions.find(
                    (option) =>
                      option.value == state[questionGroups.complianceQualityAssurance]?.status,
                  ) || compliantStatusOptions[0]
                }
                onChange={handleChangeSelect(questionGroups.complianceQualityAssurance, 'status')}
                options={compliantStatusOptions}
              />
            </div>
          </div>
        </Margin>
        <Margin left="20" bottom="100">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Heading>
                  {t('page.user.cis.certificationReports.performanceItem')}
                </Table.Heading>
                <Table.Heading>
                  {t('page.user.cis.certificationReports.referenceToICR')}
                </Table.Heading>
                <Table.Heading>
                  {t('page.user.cis.certificationReports.CBsHighlightsReport')}
                </Table.Heading>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <Table.Row>
                <Table.Cell>The CB’s outcome of its management reviews</Table.Cell>
                <Table.Cell>10.1.1, 10.1.6, 10.1.7,</Table.Cell>
                <Table.Cell>
                  Report conclusions and actions related to improvement of certification process and
                  management system{' '}
                  <Field>
                    <textarea
                      value={state[questions.managementReveiews]?.answer?.answerString}
                      onChange={handleChangeNative(questions.managementReveiews, 'string')}
                      placeholder="Write something in"
                      readOnly={appType === 'ipma' || !maEditable}
                    />
                  </Field>
                </Table.Cell>
                <Margin left="50">
                  <ButtonSearch
                    icon="comment"
                    color={
                      state[questions.managementReveiews]?.answer?.answerComment && appType === 'ma'
                        ? 'red'
                        : 'black'
                    }
                    secondary
                    rounded
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                    disabled={
                      !state[questions.managementReveiews]?.answer?.answerComment &&
                      !(appType === 'ipma' && action === 'edit')
                    }
                    onClick={() => {
                      setSelectedQuestion(state[questions.managementReveiews]);
                      setIsModalOpen(true);
                    }}
                  />
                </Margin>
              </Table.Row>
              <Table.Row>
                <Table.Cell>The CB’s outcome of internal audits</Table.Cell>
                <Table.Cell>10.1.1, 10.1.6, 10.1.8</Table.Cell>
                <Table.Cell>
                  Report conclusions and actions related to improvement of certification process and
                  management system, based on the last available report.
                  <Field>
                    <textarea
                      value={state[questions.internalAudits]?.answer?.answerString}
                      onChange={handleChangeNative(questions.internalAudits, 'string')}
                      placeholder="Write something in"
                      readOnly={appType === 'ipma' || !maEditable}
                    />
                  </Field>
                </Table.Cell>
                <Margin left="50">
                  <ButtonSearch
                    icon="comment"
                    color={
                      state[questions.internalAudits]?.answer?.answerComment && appType === 'ma'
                        ? 'red'
                        : 'black'
                    }
                    secondary
                    rounded
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                    disabled={
                      !state[questions.internalAudits]?.answer?.answerComment &&
                      !(appType === 'ipma' && action === 'edit')
                    }
                    onClick={() => {
                      setSelectedQuestion(state[questions.internalAudits]);
                      setIsModalOpen(true);
                    }}
                  />
                </Margin>
              </Table.Row>
            </Table.Body>
          </Table>
        </Margin>
        <Margin bottom="20">
          <Heading size="h5">
            {t('page.user.cis.certificationReports.ipmaYearbookInformation')}
          </Heading>
        </Margin>
        <Margin bottom="20">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Heading size="h4">
              {t('page.user.cis.certificationReports.nationalCompetenceBaseline')}
            </Heading>
          </div>
        </Margin>
        <Margin left="20" bottom="100">
          <hr />
          <div style={{ display: 'flex' }}>
            <Table>
              <Table.Body>
                <Table.Row>
                  <Table.Cell>Competence Baseline 4-L-C</Table.Cell>
                  <Table.Cell>
                    <Field>
                      <textarea
                        value={state[questions.competenceBaseline4lc]?.answer?.answerString}
                        onChange={handleChangeNative(questions.competenceBaseline4lc, 'string')}
                        placeholder="Write something in"
                        readOnly={appType === 'ipma' || !maEditable}
                      />
                    </Field>
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
            <Margin left="50">
              <ButtonSearch
                icon="comment"
                color={
                  state[questions.competenceBaseline4lc]?.answer?.answerComment && appType === 'ma'
                    ? 'red'
                    : 'black'
                }
                secondary
                rounded
                style={{
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                }}
                disabled={
                  !state[questions.competenceBaseline4lc]?.answer?.answerComment &&
                  !(appType === 'ipma' && action === 'edit')
                }
                onClick={() => {
                  setSelectedQuestion(state[questions.competenceBaseline4lc]);
                  setIsModalOpen(true);
                }}
              />
            </Margin>
          </div>
        </Margin>
        <Margin bottom="20">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Heading size="h5">
              {t(
                'page.user.cis.certificationReports.CBsCapabilityToOperateCertificationInAdictionalLanguages',
              )}
            </Heading>
          </div>
        </Margin>
        <Margin left="20" bottom="100">
          <div style={{ display: 'flex' }}>
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Heading>Additional languages</Table.Heading>
                  <Table.Heading>IPMA Certification Levels</Table.Heading>
                  {appType === 'ma' && (action === 'create' || action === 'edit') && (
                    <Table.Heading>
                      <ButtonIcon
                        style={{ float: 'right' }}
                        icon="add"
                        onClick={handleAddComplexRow(questions.cbCertCapabilityAL, {
                          language: '',
                          certLevels: '',
                        })}
                      />
                    </Table.Heading>
                  )}
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {state[questions.cbCertCapabilityAL]?.answer?.answerComplex?.map((item, index) => (
                  <Table.Row key={index}>
                    <Table.Cell>
                      <Field>
                        <input
                          value={item.language}
                          readOnly={appType === 'ipma' || !maEditable}
                          onChange={handleChangeCell(
                            questions.cbCertCapabilityAL,
                            index,
                            'language',
                          )}
                          placeholder="Write something in"
                        />
                      </Field>
                    </Table.Cell>
                    <Table.Cell>
                      <Field>
                        <input
                          readOnly={appType === 'ipma' || !maEditable}
                          value={item.certLevels}
                          onChange={handleChangeCell(
                            questions.cbCertCapabilityAL,
                            index,
                            'certLevels',
                          )}
                          placeholder="Write something in"
                        />
                      </Field>
                    </Table.Cell>
                    {appType === 'ma' && (action === 'create' || action === 'edit') && (
                      <Table.Cell
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <ButtonIcon
                          icon="trash"
                          onClick={handleRemoveComplexRow(questions.cbCertCapabilityAL, index)}
                          disabled={appType === 'ipma'}
                        />
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            <Margin left="50">
              <ButtonSearch
                icon="comment"
                color={
                  state[questions.cbCertCapabilityAL]?.answer?.answerComment && appType === 'ma'
                    ? 'red'
                    : 'black'
                }
                secondary
                rounded
                style={{
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                }}
                disabled={
                  !state[questions.cbCertCapabilityAL]?.answer?.answerComment &&
                  !(appType === 'ipma' && action === 'edit')
                }
                onClick={() => {
                  setSelectedQuestion(state[questions.cbCertCapabilityAL]);
                  setIsModalOpen(true);
                }}
              />
            </Margin>
          </div>
          <div style={{ width: '200px', marginTop: 10 }}>
            {maEditable ? (
              <FileInput
                onChange={handleChangeFileNative(questions.cbCertCapabilityAL)}
                clear={handleClearFileNative(questions.cbCertCapabilityAL)}
                fileName={
                  state[questions.cbCertCapabilityAL]?.answer?.file?.name ||
                  state[questions.cbCertCapabilityAL]?.answer?.file?.fileName ||
                  ''
                }
                placeholder="Add a file"
              />
            ) : (
              state[questions.cbCertCapabilityAL]?.answer?.file?.fileName && (
                <div style={{ marginTop: 8 }}>
                  <a
                    href={state[questions.cbCertCapabilityAL]?.answer?.file?.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {state[questions.cbCertCapabilityAL]?.answer?.file?.fileName}
                  </a>
                </div>
              )
            )}
          </div>
        </Margin>
        <Margin bottom="20">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Heading size="h5">
              {t('page.user.cis.certificationReports.certificationSystemPanelParticipation')}
            </Heading>
          </div>
        </Margin>
        <Margin left="20" bottom="100">
          <div style={{ display: 'flex' }}>
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Heading>Date/place</Table.Heading>
                  <Table.Heading>Name</Table.Heading>
                  <Table.Heading>Role in CB</Table.Heading>
                  {appType === 'ma' && (action === 'create' || action === 'edit') && (
                    <Table.Heading>
                      <ButtonIcon
                        style={{ float: 'right' }}
                        icon="add"
                        onClick={handleAddComplexRow(questions.cspp, {
                          datePlace: '',
                          name: '',
                          role: '',
                        })}
                      />
                    </Table.Heading>
                  )}
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {state[questions.cspp]?.answer?.answerComplex?.map((item, index) => (
                  <Table.Row>
                    <Table.Cell>
                      <Field>
                        <input
                          value={item.datePlace}
                          onChange={handleChangeCell(questions.cspp, index, 'datePlace')}
                          placeholder="Write something in"
                          readOnly={appType === 'ipma' || !maEditable}
                        />
                      </Field>
                    </Table.Cell>
                    <Table.Cell>
                      <Field>
                        <input
                          value={item.name}
                          onChange={handleChangeCell(questions.cspp, index, 'name')}
                          placeholder="Write something in"
                          readOnly={appType === 'ipma' || !maEditable}
                        />
                      </Field>
                    </Table.Cell>
                    <Table.Cell>
                      <Field>
                        <input
                          value={item.role}
                          onChange={handleChangeCell(questions.cspp, index, 'role')}
                          placeholder="Write something in"
                          readOnly={appType === 'ipma' || !maEditable}
                        />
                      </Field>
                    </Table.Cell>
                    {appType === 'ma' && (action === 'create' || action === 'edit') && (
                      <Table.Cell
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <ButtonIcon
                          icon="trash"
                          onClick={handleRemoveComplexRow(questions.cspp, index)}
                        />
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            <Margin left="50">
              <ButtonSearch
                icon="comment"
                color={
                  state[questions.cspp]?.answer?.answerComment && appType === 'ma' ? 'red' : 'black'
                }
                secondary
                rounded
                style={{
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                }}
                disabled={
                  !(
                    state[questions.cspp]?.answer?.answerComment &&
                    !(appType === 'ipma' && action === 'edit')
                  )
                }
                onClick={() => {
                  setSelectedQuestion(state[questions.cspp]);
                  setIsModalOpen(true);
                }}
              />
            </Margin>
          </div>
          <div style={{ width: '200px', marginTop: 10 }}>
            {maEditable ? (
              <FileInput
                placeholder="Add a file"
                onChange={handleChangeFileNative(questions.cspp)}
                clear={handleClearFileNative(questions.cspp)}
                fileName={
                  state[questions.cspp]?.answer?.file?.name ||
                  state[questions.cspp]?.answer?.file?.fileName
                }
              />
            ) : (
              <div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: state[questions.cspp]?.answer?.answerString?.toString('html') || '',
                  }}
                />
                {state[questions.cspp]?.answer?.file?.fileName && (
                  <div style={{ marginTop: 8 }}>
                    <a
                      href={state[questions.cspp]?.answer?.file?.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {state[questions.cspp]?.answer?.file?.fileName}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </Margin>
        <Modal
          heading="Comment on answer"
          isOpen={isModalOpen && selectedQuestion}
          description={
            appType === 'ipma' && action === 'edit' ? (
              <Field>
                <input
                  ref={modalInputRef}
                  type="text"
                  defaultValue={selectedQuestion?.answer?.answerComment}
                />
              </Field>
            ) : (
              selectedQuestion?.answer?.answerComment
            )
          }
          {...(!(appType === 'ipma' && action === 'edit') && {
            customActions: () => (
              <Row>
                <Col size={{ xs: 1 / 2 }}>
                  <Button onClick={() => setIsModalOpen(false)} bordered>
                    {t('global.closeButton')}
                  </Button>
                </Col>
              </Row>
            ),
          })}
          confirm={() => {
            modalInputRef.current &&
              handleChangeCustom(selectedQuestion.name, 'comment')(modalInputRef.current.value);

            setIsModalOpen(false);
          }}
          cancel={() => setIsModalOpen(false)}
        />
      </Container>

      {portalReady &&
        ReactDOM.createPortal(
          appType === 'ma' && (action === 'create' || action === 'edit') ? (
            <Row style={{ alignItems: 'center', justifyContent: 'end' }}>
              <Col size={{ xl: 1 / 3 }}>
                <Margin>
                  <Button secondary onClick={() => submit('Draft')}>
                    {t('global.saveAsDraftButton')}
                  </Button>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 3 }}>
                <Margin>
                  <Button secondary onClick={() => submit('Submitted')}>
                    {t('global.sendToCVMBButton')}
                  </Button>
                </Margin>
              </Col>
            </Row>
          ) : appType === 'ipma' && action === 'edit' ? (
            <Row style={{ alignItems: 'center', justifyContent: 'end' }}>
              <Col size={{ xl: 1 / 3 }}>
                <Margin>
                  <Button secondary onClick={() => submit('Submitted')}>
                    Save Review Progress
                  </Button>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 6 }}>
                <Margin>
                  <Button secondary onClick={() => submit('Accepted')}>
                    {t('global.acceptButton')}
                  </Button>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 6 }}>
                <Margin>
                  <Button secondary onClick={() => submit('Reviewed')}>
                    {t('global.rejectButton')}
                  </Button>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 3 }}>
                <Margin>
                  <Button secondary onClick={() => fetchPdfCisReports(reports.id)}>
                    {t('page.user.cis.certificationReports.exportToPdf')}
                  </Button>
                </Margin>
              </Col>
            </Row>
          ) : null,
          document.getElementById(`${type}-cis-buttons-portal`),
        )}
    </PageContent>
  );
};

export default CertificationReportForm;
